// LEGAL STATUS: To be legally validated before commercial production (EN translation of fr/SubprocessorsPage.js).
import { Page, H1, H2, Lead, P, Notice } from "../../public/primitives";

// Allowed statuses: "Active", "Planned / not enabled", "To be confirmed" (same rules as the FR source).
const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  planned: "bg-slate-100 text-slate-500 border-slate-200",
  tbc: "bg-amber-100 text-amber-700 border-amber-200",
};
const STATUS_LABEL = { active: "Active", planned: "Planned / not enabled", tbc: "To be confirmed" };

const SUBPROCESSORS = [
  {
    name: "Microsoft 365 (SMTP)",
    purpose: "Sending transactional emails (notifications, verification, invitations)",
    data: "Email address, notification content (never the secret or the key)",
    location: "To be confirmed",
    status: "active",
  },
  {
    name: "Bird (SMS)",
    purpose: "SMS delivery",
    data: "Currently in simulated mode — no real data transmitted",
    location: "—",
    status: "planned",
  },
  {
    name: "Dolibarr (billing)",
    purpose: "Invoice generation (future commercial use)",
    data: "—",
    location: "To be confirmed",
    status: "planned",
  },
  {
    name: "Hosting provider",
    purpose: "Hosting of the service infrastructure",
    data: "—",
    location: "To be confirmed",
    status: "tbc",
  },
];

const EXTERNAL = [
  ["Service", "Have I Been Pwned — Pwned Passwords"],
  ["Purpose", "Checking for compromised passwords"],
  ["Trigger", "Only after an explicit user action"],
  ["Application data transmitted", "First 5 characters of the SHA-1 hash"],
  ["Password", "Never transmitted"],
  ["Full hash", "Never transmitted"],
  ["Network data", "Usual data associated with an Internet request, in particular the public IP address"],
];

export const SubprocessorsPage = () => (
  <Page testid="subprocessors-page">
    <H1>Subprocessors</H1>
    <Lead>
      This page lists the confirmed providers, as well as the technical components whose provider or some
      information remains to be finalised before production. Each row shows its activation status; no
      unconfirmed provider is presented as active.
    </Lead>

    <div data-testid="subprocessors-table" className="overflow-x-auto rounded-xl border border-slate-200 bg-white my-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left">
            <th className="py-2.5 px-4 font-semibold text-slate-700">Provider</th>
            <th className="py-2.5 px-4 font-semibold text-slate-700">Purpose</th>
            <th className="py-2.5 px-4 font-semibold text-slate-700">Data</th>
            <th className="py-2.5 px-4 font-semibold text-slate-700">Location</th>
            <th className="py-2.5 px-4 font-semibold text-slate-700">Status</th>
          </tr>
        </thead>
        <tbody>
          {SUBPROCESSORS.map((s) => (
            <tr key={s.name} className="border-t border-slate-100 align-top">
              <td className="py-2.5 px-4 text-slate-800 font-medium">{s.name}</td>
              <td className="py-2.5 px-4 text-slate-600">{s.purpose}</td>
              <td className="py-2.5 px-4 text-slate-600">{s.data}</td>
              <td className="py-2.5 px-4 text-slate-600">{s.location}</td>
              <td className="py-2.5 px-4">
                <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[s.status]}`}>
                  {STATUS_LABEL[s.status]}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <P className="text-slate-500">
      “Active”: provider in production. “Planned / not enabled”: integration prepared but not used. “To be
      confirmed”: information not yet settled.
    </P>

    <Notice testid="subprocessors-note">
      The data location is not invented: it will be specified once the hosting provider and the other
      providers are definitively confirmed.
    </Notice>

    <H2 id="services-externes">External services called voluntarily</H2>
    <P>
      Some features explicitly triggered by the user send a request to an external service. These services
      are not data processing subprocessors in the usual sense: they are called on demand, without
      transmitting the sensitive content.
    </P>
    <div data-testid="external-services-table" className="overflow-x-auto rounded-xl border border-slate-200 bg-white my-4">
      <table className="w-full text-sm">
        <tbody>
          {EXTERNAL.map(([k, v], i) => (
            <tr key={k} className={i < EXTERNAL.length - 1 ? "border-b border-slate-100" : ""}>
              <td className={`py-2.5 px-4 font-medium text-slate-800 ${i === 0 ? "w-1/3" : ""}`}>{k}</td>
              <td className="py-2.5 px-4 text-slate-600">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Page>
);

export default SubprocessorsPage;
